import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "button", "dropzone"];

  connect() {
    this.preventDefaults = this.preventDefaults.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);

    // Prevent default drag behaviors
    ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
      this.element.addEventListener(eventName, this.preventDefaults, false);
    });

    // Highlight dropzone when item is dragged over it
    ["dragenter", "dragover"].forEach((eventName) => {
      this.dropzoneTarget.addEventListener(
        eventName,
        () => {
          this.dropzoneTarget.classList.add(
            "border-blue-500",
            "dark:border-blue-500",
          );
          this.dropzoneTarget.classList.remove(
            "border-slate-300",
            "dark:border-slate-700",
          );
        },
        false,
      );
    });

    ["dragleave", "drop"].forEach((eventName) => {
      this.dropzoneTarget.addEventListener(
        eventName,
        () => {
          this.dropzoneTarget.classList.remove(
            "border-blue-500",
            "dark:border-blue-500",
          );
          this.dropzoneTarget.classList.add(
            "border-slate-300",
            "dark:border-slate-700",
          );
        },
        false,
      );
    });

    // Handle dropped files
    this.element.addEventListener("drop", this.handleDrop, false);

    // Handle standard file selection
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("change", this.handleInputChange);
    }
  }

  disconnect() {
    ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
      this.dropzoneTarget.removeEventListener(
        eventName,
        this.preventDefaults,
        false,
      );
    });
    this.element.removeEventListener("drop", this.handleDrop, false);
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("change", this.handleInputChange);
    }
  }

  preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleDrop(e) {
    let dt = e.dataTransfer;
    let files = dt.files;
    this.handleFiles(files);
  }

  handleInputChange(e) {
    let files = e.target.files;
    this.handleFiles(files);
    // Clear the input so selecting the same file again triggers change event
    if (this.hasInputTarget) {
      this.inputTarget.value = "";
    }
  }

  handleFiles(files) {
    if (files.length === 0) return;

    // We assume the nested form component's add button is available
    if (!this.hasButtonTarget) {
      console.error("Dropzone cannot find the nested form 'add' button.");
      return;
    }

    const validFiles = [...files].filter(
      (file) =>
        file.type.startsWith("image/") || file.type.startsWith("video/"),
    );
    const invalidFiles = [...files].filter(
      (file) =>
        !file.type.startsWith("image/") && !file.type.startsWith("video/"),
    );

    if (invalidFiles.length > 0) {
      alert("Some files were skipped. Only images and videos are allowed!");
    }

    // Get the base number of file inputs *before* we start adding new ones
    const baseInputCount =
      this.element.querySelectorAll('input[type="file"]').length;

    validFiles.forEach((file, index) => {
      // Stagger the entire row creation sequence so the nested-form controller
      // generates a unique timestamp (ID) for each row.
      setTimeout(() => {
        // 1. Click the nested form "add" button to generate a new row
        this.buttonTarget.click();

        // 2. We use setTimeout to allow the DOM to update. We calculate the expected index
        // of the NEW file input based on how many inputs existed before the loop + the current loop index
        setTimeout(() => {
          const fileInputs =
            this.element.querySelectorAll('input[type="file"]');
          const targetInputIndex = baseInputCount + index;

          if (targetInputIndex < fileInputs.length) {
            const targetInput = fileInputs[targetInputIndex];

            // Create a new DataTransfer object and add our single file
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);

            // Assign the files to the input
            targetInput.files = dataTransfer.files;

            // Dispatch a change event so any other listeners (like image previews) update
            targetInput.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }, 30); // Wait for DOM injection
      }, 50 * index); // Stagger each file upload sequence by 50ms
    });
  }
}
